import { useState } from 'react';
import { useForm } from 'react-hook-form';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';

interface CreateUserFormData {
  name: string;
  email: string;
  password: string;
  role: 'admin' | 'user';
}

interface CreateUserModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: CreateUserFormData) => void;
  isLoading?: boolean;
  error?: string | null;
}

export function CreateUserModal({
  open,
  onClose,
  onSubmit,
  isLoading = false,
  error = null,
}: CreateUserModalProps) {
  const [showPassword, setShowPassword] = useState(false);
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<CreateUserFormData>({
    defaultValues: {
      name: '',
      email: '',
      password: '',
      role: 'user',
    },
  });

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleFormSubmit = (data: CreateUserFormData) => {
    onSubmit(data);
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit(handleFormSubmit)}>
        <DialogTitle>새 사용자 생성</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <TextField
            {...register('name', {
              required: '이름을 입력해주세요',
              minLength: { value: 2, message: '이름은 2자 이상이어야 합니다' },
            })}
            label="이름"
            fullWidth
            margin="normal"
            error={!!errors.name}
            helperText={errors.name?.message}
            disabled={isLoading}
          />
          <TextField
            {...register('email', {
              required: '이메일을 입력해주세요',
              pattern: {
                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                message: '올바른 이메일 형식이 아닙니다',
              },
            })}
            label="이메일"
            type="email"
            fullWidth
            margin="normal"
            error={!!errors.email}
            helperText={errors.email?.message}
            disabled={isLoading}
          />
          <TextField
            {...register('password', {
              required: '비밀번호를 입력해주세요',
              minLength: { value: 8, message: '비밀번호는 8자 이상이어야 합니다' },
            })}
            label="임시 비밀번호"
            type={showPassword ? 'text' : 'password'}
            fullWidth
            margin="normal"
            error={!!errors.password}
            helperText={errors.password?.message}
            disabled={isLoading}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <FormControl fullWidth margin="normal">
            <InputLabel id="create-role-label">역할</InputLabel>
            <Select
              {...register('role')}
              labelId="create-role-label"
              label="역할"
              defaultValue="user"
              disabled={isLoading}
            >
              <MenuItem value="admin">관리자</MenuItem>
              <MenuItem value="user">일반 사용자</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={isLoading}>
            취소
          </Button>
          <Button type="submit" variant="contained" disabled={isLoading}>
            {isLoading ? <CircularProgress size={24} /> : '생성'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
